import { ui } from '@clientio/jointjs'
import { Actions } from '../../../../../../../../store/state'
import { ContextMenuElementType } from '../../../core/ui-configs'
import { getLinkToolConfig, getNodeToolConfig } from '../plugins'

/**
 * Enum for Paper events.
 * @readonly
 * @enum {string}
 */
export const PaperEvents = {
  // Pointerdown
  CellPointerDown: 'cell:pointerdown',
  LinkPointerDown: 'link:pointerdown',
  BlankPointerDown: 'blank:pointerdown',
  ElementPointerDown: 'element:pointerdown',

  // Pointermove
  CellPointerDrag: 'cell:pointermove',
  LinkPointerDrag: 'link:pointermove',
  BlankPointerDrag: 'blank:pointermove',
  ElementPointerDrag: 'element:pointermove',

  // Pointerup
  CellPointerUp: 'cell:pointerup',
  LinkPointerUp: 'link:pointerup',
  BlankPointerUp: 'blank:pointerup',
  ElementPointerUp: 'element:pointerup',

  // Pointerclick
  CellPointerClick: 'cell:pointerclick',
  LinkPointerClick: 'link:pointerclick',
  BlankPointerClick: 'blank:pointerclick',
  ElementPointerClick: 'element:pointerclick',

  // PointerDoubleClick
  CellPointerDoubleClick: 'cell:pointerdblclick',
  LinkPointerDoubleClick: 'link:pointerdblclick',
  BlankPointerDoubleClick: 'blank:pointerdblclick',
  ElementPointerDoubleClick: 'element:pointerdblclick',

  // mouse events
  ElementMouseOver: 'element:mouseover',
  ElementMouseOut: 'element:mouseout',

  // Context menu
  CellRightClick: 'cell:contextmenu',
  LinkRightClick: 'link:contextmenu',
  ElementRightClick: 'element:contextmenu',
  BlankRightClick: 'blank:contextmenu',

  // Link Mouse Events
  LinkMouseEnter: 'link:mouseenter',
  LinkMouseLeave: 'link:mouseleave',

  // Util
  PaperPinch: 'paper:pinch',
  LinkConnect: 'link:connect',
  ConnectionsBtnClick: 'node:connectionBtnClick',
  NodeChipClick: 'node:chip',

  // Halo Search
  HaloSearchInput: 'halo:search:input',
  HaloSearchFocus: 'halo:search:focus',
  HaloSearchBlur: 'halo:search:blur',

  // Remove Buttons
  LinkRemoveBtnClick: 'link:removeBtnClick',
  NodeRemoveBtnClick: 'node:removeBtnClick'
}

export const KeyboardEventEnum = {
  CtrlC: 'ctrl+c',
  CtrlX: 'ctrl+x',
  CtrlV: 'ctrl+v',
  CtrlZ: 'ctrl+z',
  CtrlShiftZ: 'ctrl+shift+z',
  Delete: 'delete'
}

const getPaletteNodesFromState = diagramController => {
  const state = diagramController.getState()
  return state?.ui?.palette?.nodes || []
}

/** @typedef {{[eventName in keyof Paper.EventMap]: Paper.EventMap[eventName]}} PaperEventsMap */

/**
 * Get the event listeners in view mode for the diagram controller.
 * @param {DiagramController} diagramController - The diagram controller instance.
 * @param {UseDispatch} dispatcher
 * @returns {PaperEventsMap} The event listeners for paper.
 */
export function getViewListeners(diagramController, dispatcher) {
  return {
    [PaperEvents.PaperPinch]: (evt, ox, oy, scale) => {
      if (evt.ctrlKey) {
        evt.preventDefault()
        const zoom = diagramController.zoomWithMouseWheel(ox, oy, scale)
        dispatcher(Actions.zoomSet(zoom))
      }
    },
    [PaperEvents.BlankPointerDown]: evt => {
      if (!evt.ctrlKey) {
        diagramController.startPanning(evt)
      }
    },
    [PaperEvents.BlankPointerClick]: () => {
      diagramController.handleBlankClick()
      dispatcher(Actions.blankClick())
    },
    [PaperEvents.ElementPointerDown]: ({ model }) => {
      //   move node to front
      model.toFront({ deep: true })
    },
    [PaperEvents.ElementPointerClick]: (elementView, evt) => {
      if (evt.detail === 2 || evt.ctrlKey || evt.data.disableClick) {
        return
      } // prevent fire action when doubleclick

      const nodeId = elementView.model.id
      dispatcher(Actions.nodeSelected(nodeId)) // select node
    },
    [`${PaperEvents.BlankPointerDown}
      ${PaperEvents.BlankPointerClick}
      ${PaperEvents.PaperPinch}
      ${PaperEvents.LinkPointerClick}`]: () => diagramController.hideTools()
  }
}

/**
 * Get the event listeners in edit mode for the diagram controller.
 * @param {DiagramController} diagramController - The diagram controller instance.
 * @param {UseDispatch} dispatcher
 * @param {SelectionController} selectionController - The diagram selection controller instance.
 * @returns {{[eventName in keyof dia.Paper.EventMap]: dia.Paper.EventMap[eventName]} | any} The event listeners for paper.
 */
export function getEditListeners(diagramController, dispatcher, selectionController) {
  return {
    [PaperEvents.CellPointerClick]: (elementView, _evt) => {
      // open replace element panel on temporary node
      const isTempNode = elementView.model.get('isTempNode')
      if (isTempNode) {
        diagramController.createQuickOptions(elementView)
      }
    },
    [PaperEvents.BlankPointerDown]: evt => {
      if (evt.ctrlKey) {
        selectionController.startLassoSelect(evt)
      }
    },
    [PaperEvents.BlankPointerUp]: evt => {
      if (evt.ctrlKey) {
        // lasso selection - by default links are not included in selection
        const selection = selectionController.getLassoSelection()
        if (selection.nodes.length) {
          dispatcher(Actions.cellsSelected(selection))
        }
      }
    },
    [PaperEvents.ElementPointerClick]: (elementView, evt) => {
      elementView.preventDefaultInteraction(evt)
      if (evt.detail === 2 || evt.ctrlKey) {
        return
      } // prevent fire action when doubleclick

      // Error Node: Check if the error icon was clicked
      // Check if click is on error icon or within error icon container
      const errorIcon = elementView.el.querySelector('.joint-node-error-icon')
      const errorContainer = elementView.el.querySelector('.joint-node-validation-icon-container')

      if (errorIcon && errorContainer) {
        const rect = errorContainer.getBoundingClientRect()
        const clickX = evt.originalEvent.clientX
        const clickY = evt.originalEvent.clientY

        // Check if click coordinates are within the error container bounds
        if (clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom) {
          dispatcher(Actions.nodeErrorClicked(elementView.model.id, errorIcon.id))
          evt.stopPropagation()
          return
        }
      }

      const isTempNode = elementView.model.get('isTempNode')
      const xPos = isTempNode ? 88 : 210
      elementView.addTools(getNodeToolConfig(xPos)) // add remove button for node

      // Always dispatch node selection on click to ensure properties panel opens
      dispatcher(Actions.nodeSelected(elementView.model.id))
    },
    [PaperEvents.ElementPointerDown]: ({ model }, evt) => {
      // Move node to front
      model.toFront({ deep: true })

      if (evt.ctrlKey) {
        const updatedSelect = selectionController.cherryPickNode(model.id)
        dispatcher(Actions.cellsSelected(updatedSelect))
      }
    },
    [PaperEvents.ElementPointerDrag]: (view, evt) => {
      evt.data.moved = true
    },
    [PaperEvents.ElementPointerUp]: ({ model }, evt) => {
      if (evt.data.moved) {
        const selectedNodes = selectionController.getSelectedNodes()
        let movedNodes = selectedNodes.map(e => ({
          id: e.id,
          position: e.position().toJSON()
        }))

        // If no nodes are selected but this node was moved, include it
        if (movedNodes.length === 0) {
          movedNodes = [
            {
              id: model.id,
              position: model.position().toJSON()
            }
          ]
        }

        dispatcher(Actions.nodesMoved(movedNodes))
      }
    },

    [PaperEvents.ElementMouseOver]: (_elementView, _evt) => {
      // dispatcher(Actions.infoPanelOpen(elementView.model.id, elementView.el))
    },
    [PaperEvents.ElementMouseOut]: (_elementView, _evt) => {
      // dispatcher(Actions.infoPanelClose())
    },

    [PaperEvents.ConnectionsBtnClick]: (elementView, evt) => {
      evt.stopPropagation()
      // prevent halo on temporary node.
      if (!elementView.model.get('preventHalo')) {
        diagramController.createQuickOptions(elementView)
      } else {
        // open replace element panel on temporary node
        const isTempNode = elementView.model.get('isTempNode')
        if (isTempNode) {
          diagramController.createQuickOptions(elementView)
        }
      }
    },
    [PaperEvents.NodeChipClick]: (elementView, evt) => {
      evt.stopPropagation()
      // add here, for additional node chip functionalities on click
    },
    [PaperEvents.LinkPointerClick]: ({ model }, evt) => {
      if (evt.detail === 2) {
        return
      } // prevent fire action when doubleclick
      if (evt.ctrlKey) {
        const updatedSelection = selectionController.cherryPickLink(model.id)
        dispatcher(Actions.cellsSelected(updatedSelection))
      } else {
        dispatcher(Actions.linkSelected(model.id))
      }
    },
    [`
    ${PaperEvents.BlankPointerDown}
           ${PaperEvents.BlankPointerClick}
           ${PaperEvents.PaperPinch}
           ${PaperEvents.LinkPointerClick}`]: () => {
      diagramController.hideTools()
    },
    [PaperEvents.LinkConnect]: (linkView, evt, targetView, magnet, arrowhead) => {
      if (arrowhead === 'target' && targetView && targetView.model.isElement()) {
        const source = linkView.model.source()
        const target = targetView.model.id
        // passing linkView to remove it in case of failure inside replaceLinkTarget
        dispatcher(Actions.linkTargetReplaced(linkView.model.id, source.id, target, linkView))

        // dispatcher(Actions.linkCreated(source.id, targetView.model.id));
        // linkView.model.remove(); // remove temporary graph link
        ui.Halo.clear(linkView.paper) // close halo
      }
    },
    [PaperEvents.LinkMouseEnter]: linkView => {
      linkView.addTools(getLinkToolConfig())
    },
    [PaperEvents.LinkMouseLeave]: linkView => {
      linkView.removeTools()
    },
    [PaperEvents.BlankRightClick]: evt => {
      dispatcher(Actions.cellsSelected({ nodes: [], links: [] }))
      const uiPosition = { x: evt.clientX, y: evt.clientY }
      const paperPosition = diagramController.getPaperPoint(uiPosition)
      dispatcher(Actions.contextMenuOpen(ContextMenuElementType.BLANK, uiPosition, paperPosition))
    },
    [PaperEvents.ElementRightClick]: ({ model }, evt) => {
      // first step: update selection if node is not selected
      if (!selectionController.isNodeSelected(model.id)) {
        dispatcher(Actions.cellsSelected({ nodes: [model.id], links: [] }))
      }
      // second step: trigger opening context-menu
      const uiPosition = { x: evt.clientX, y: evt.clientY }
      const paperPosition = diagramController.getPaperPoint(uiPosition)
      dispatcher(Actions.contextMenuOpen(ContextMenuElementType.NODE, uiPosition, paperPosition))
    },
    [PaperEvents.LinkRightClick]: ({ model }, evt) => {
      // first step: update selection if link is not selected
      if (!selectionController.isLinkSelected(model.id)) {
        dispatcher(Actions.linkSelected(model.id))
      }
      // second step: trigger opening context-menu
      const uiPosition = { x: evt.clientX, y: evt.clientY }
      const paperPosition = diagramController.getPaperPoint(uiPosition)
      dispatcher(Actions.contextMenuOpen(ContextMenuElementType.LINK, uiPosition, paperPosition))
    },
    [PaperEvents.LinkRemoveBtnClick]: linkView => {
      dispatcher(Actions.linkRemoved(linkView.model.id))
    },
    [PaperEvents.NodeRemoveBtnClick]: elementView => {
      dispatcher(Actions.nodeRemoved(elementView.model.id))
    },
    [PaperEvents.CellPointerDoubleClick]: (cellView, evt) => {
      // Only open text editor for links on double-click, not for nodes
      // Node text editing is handled by specific label/description double-click handlers
      if (cellView.model.isLink()) {
        diagramController.hideTools()
        cellView.model.toFront()
        diagramController.openTextEditor(cellView, evt)
      }
    },
    'element:label:pointerdown': (cellView, evt) => {
      // Handle double-click detection for label editing
      const currentTime = Date.now()
      const lastClickTime = cellView._lastLabelClickTime || 0
      const timeDiff = currentTime - lastClickTime

      cellView._lastLabelClickTime = currentTime

      // Double-click detection (within 300ms)
      if (timeDiff < 300) {
        evt.data = { ...evt.data, description: false }
        diagramController.openTextEditor(cellView, evt)
      }
    },
    'element:description:pointerdown': (cellView, evt) => {
      // Handle double-click detection for description editing
      const currentTime = Date.now()
      const lastClickTime = cellView._lastDescriptionClickTime || 0
      const timeDiff = currentTime - lastClickTime

      cellView._lastDescriptionClickTime = currentTime

      // Double-click detection (within 300ms)
      if (timeDiff < 300) {
        evt.data = { ...evt.data, description: true }
        diagramController.openTextEditor(cellView, evt)
      }
    },
    [KeyboardEventEnum.CtrlZ]: () => {
      dispatcher(Actions.undo())
    },
    [KeyboardEventEnum.CtrlShiftZ]: () => {
      dispatcher(Actions.redo())
    },
    [KeyboardEventEnum.CtrlC]: () => {
      dispatcher(Actions.copySelection())
    },
    [KeyboardEventEnum.CtrlX]: () => {
      dispatcher(Actions.cutSelection())
    },
    [KeyboardEventEnum.CtrlV]: () => {
      const viewportCenter = diagramController.getViewportCenterPoint()
      dispatcher(Actions.pasteSelection(viewportCenter))
    },
    [KeyboardEventEnum.Delete]: () => {
      const { nodes = [], links = [] } = diagramController.getState()?.ui?.selection || {}
      const isBulk = nodes.length + links.length > 1
      dispatcher(Actions.removeSelection(isBulk))
    },
    [PaperEvents.HaloSearchInput]: (searchTerm, sourceNodeId) => {
      // Filter nodes based on search term and update halo panel
      // get palette nodes from state ui.palette.nodes
      const paletteNodes = getPaletteNodesFromState(diagramController)

      diagramController.filterHaloNodes(searchTerm, sourceNodeId, paletteNodes)
    }
    // [PaperEvents.HaloSearchFocus]: (sourceNodeId) => {
    //   // Handle search input focus
    //   console.info('Search focused for node:', sourceNodeId)
    // },
    // [PaperEvents.HaloSearchBlur]: (sourceNodeId) => {
    //   // Handle search input blur
    //   console.info('Search blurred for node:', sourceNodeId)
    // }
  }
}
